// 🌐 CLIENTE HTTP DO RAG SERVIDOR-SIDE
// Sistema: HEPATO TX RONDÔNIA — Camada de comunicação para B95 Caminho B
// ─────────────────────────────────────────────────────────────
// Substitui as 3 chamadas diretas que hoje expõem chaves no bundle:
//   - Voyage embeddings (generateQueryEmbedding em embeddings.ts)
//   - Pinecone query (vectorStore.query em vectorStore.ts)
//   - Voyage rerank (rerankChunks em reranker.ts)
//
// Pipeline preservado bit-a-bit (Caminho B do B95):
//   1. buscarChunksDensos() → POST /api/rag-search
//      (doRerank=false, returnAllCandidates=true)
//      retorna candidatos densos brutos para alimentar RRF híbrido client-side
//   2. rerankChunksRemoto() → POST /api/rag-rerank
//      faz cross-encoder rerank dos chunks fundidos (dense + BM25)
//
// B357 (AI_156): o BM25 roda no servidor (buscarChunksHibridos); o RRF segue no
// client (fusaoRRF.ts).
//
// Auth:
//   - Firebase ID Token (Authorization: Bearer <token>)
//   - getIdToken() default (false) — usa cache, refresh automático se expirado
//   - Em 401, retry uma vez com getIdToken(true) para forçar refresh
//     (cobre janela rara onde token está vivo no client mas morto no server)
//
// Degradação graciosa:
//   - auth.currentUser === null (usuário deslogado, race com browserSessionPersistence)
//     → retorna [] silenciosamente (caller decide o que fazer)
//   - 401/403 não recuperáveis → throw (caller decide propagar ou capturar)
//   - 408/500 → throw (transientes, caller pode fazer retry estratégico)
// ─────────────────────────────────────────────────────────────

import { postAutenticado } from "../../../../lib/authFetch";
import { SearchResult, GuidelineCategory } from "../types";

const ENDPOINT_SEARCH = "/api/rag-search";
const ENDPOINT_RERANK = "/api/rag-rerank";

// ─────────────────────────────────────────────────────────────
// POST autenticado: fonte única em src/lib/authFetch.ts (B131/
// B204, AI_94) — getIdTokenSafe + retry em 401 viviam duplicados
// aqui e em analyzeClient.ts. Semântica preservada: null quando
// deslogado (degradação graciosa decidida pelos callers abaixo).
// ─────────────────────────────────────────────────────────────
function fetchAuth<T>(
  endpoint: string,
  body: Record<string, unknown>,
  contexto: string
): Promise<T | null> {
  return postAutenticado<T>(endpoint, body, `ragClient ${contexto}`);
}

// ═════════════════════════════════════════════════════════════
// FUNÇÃO 1 — Busca densa: dense + Pinecone + dedup (sem rerank)
// ═════════════════════════════════════════════════════════════
// Substitui o ciclo "enrichQuery → generateQueryEmbedding → pinecone.query"
// que hoje vive em buscarContexto.ts/buscarContextoGuidelines() + repetições
// em buscarContextoMultiplo().
//
// O backend faz embeddings + pinecone + dedup em uma chamada e devolve TODOS
// os candidatos brutos (returnAllCandidates: true). O client então alimenta
// esses candidatos ao RRF híbrido com BM25 e ao rerank cross-encoder.
//
// Em caso de usuário deslogado, retorna [] silenciosamente.
// Erros transientes (500, 408, etc.) propagam via throw.
// ═════════════════════════════════════════════════════════════
export async function buscarChunksDensos(
  queries: string[],
  options?: {
    categories?: GuidelineCategory[];
    organizations?: string[];
    topK?: number;
  }
): Promise<SearchResult[]> {
  if (!queries || queries.length === 0) return [];

  const startTime = Date.now();
  const resp = await fetchAuth<{
    chunks: SearchResult[];
    timings: {
      embeddingsMs: number;
      pineconeMs: number;
      rerankMs: number;
      totalMs: number;
    };
    deduplicatedFrom: number;
  }>(
    ENDPOINT_SEARCH,
    {
      queries: queries.slice(0, 6),
      categories: options?.categories,
      organizations: options?.organizations,
      topK: options?.topK ?? 12,
      doRerank: false,
      returnAllCandidates: true,
    },
    "buscarChunksDensos"
  );

  if (!resp) {
    // Usuário deslogado — degradação graciosa
    return [];
  }

  const elapsed = Date.now() - startTime;
  console.log(
    `🌐 [ragClient] dense: ${resp.chunks.length} chunks (de ${resp.deduplicatedFrom} brutos) em ${elapsed}ms ` +
      `(server: emb=${resp.timings.embeddingsMs}ms, pc=${resp.timings.pineconeMs}ms)`
  );

  return resp.chunks;
}

// ═════════════════════════════════════════════════════════════
// FUNÇÃO 1b — Busca HÍBRIDA (denso + BM25 no servidor) — B357 (AI_156)
// ═════════════════════════════════════════════════════════════
// Mesmo endpoint, agora com `queriesBm25`: o servidor roda a busca por palavras
// (BM25) e devolve os trechos achados por ela JÁ COM TEXTO (antes o índice de
// 20 MB era baixado pelo navegador e os achados só-BM25 eram descartados por
// falta de texto). O RRF, o boost por sociedade e o rerank continuam no client.
// Até 7 queries (antes 6 — a 7ª, a PT de situação especial, ficava de fora).
// ═════════════════════════════════════════════════════════════
export async function buscarChunksHibridos(
  queriesDensas: string[],
  queriesBm25: string[],
  options?: {
    categories?: GuidelineCategory[];
    organizations?: string[];
    topK?: number;
  }
): Promise<{ densos: SearchResult[]; bm25: SearchResult[] }> {
  if (!queriesDensas || queriesDensas.length === 0) {
    return { densos: [], bm25: [] };
  }

  const startTime = Date.now();
  const resp = await fetchAuth<{
    chunks: SearchResult[];
    chunksBm25?: SearchResult[];
    bm25Disponivel?: boolean;
    timings: {
      embeddingsMs: number;
      pineconeMs: number;
      rerankMs: number;
      bm25Ms?: number;
      totalMs: number;
    };
    deduplicatedFrom: number;
  }>(
    ENDPOINT_SEARCH,
    {
      queries: queriesDensas.slice(0, 7),
      queriesBm25:
        queriesBm25.length > 0 ? queriesBm25.slice(0, 7) : undefined,
      categories: options?.categories,
      organizations: options?.organizations,
      topK: options?.topK ?? 12,
      doRerank: false,
      returnAllCandidates: true,
    },
    "buscarChunksHibridos"
  );

  if (!resp) {
    // Usuário deslogado — degradação graciosa
    return { densos: [], bm25: [] };
  }

  const bm25 = resp.chunksBm25 || [];
  const elapsed = Date.now() - startTime;
  console.log(
    `🌐 [ragClient] híbrido: ${resp.chunks.length} densos (de ${resp.deduplicatedFrom} brutos) + ` +
      `${bm25.length} BM25${resp.bm25Disponivel === false ? " (índice indisponível)" : ""} em ${elapsed}ms ` +
      `(server: emb=${resp.timings.embeddingsMs}ms, pc=${resp.timings.pineconeMs}ms, bm25=${resp.timings.bm25Ms ?? "-"}ms)`
  );

  return { densos: resp.chunks, bm25 };
}

// ═════════════════════════════════════════════════════════════
// FUNÇÃO 2 — Rerank cross-encoder remoto (Voyage rerank-2)
// ═════════════════════════════════════════════════════════════
// Substitui rerankChunks() em reranker.ts.
// Espera receber chunks já fundidos (dense + BM25 via RRF) com texto preenchido.
//
// Otimização local: se chunks.length <= topK, retorna como está
// (espelha o skip do backend; evita round-trip desnecessário).
//
// Em caso de usuário deslogado, retorna chunks.slice(0, topK) (fallback de
// ordem original) em vez de [], porque o caller já tem chunks válidos —
// devolver vazio descartaria informação.
// ═════════════════════════════════════════════════════════════
export async function rerankChunksRemoto(
  query: string,
  chunks: SearchResult[],
  topK: number
): Promise<SearchResult[]> {
  if (!chunks || chunks.length === 0) return [];

  // Otimização: skip do round-trip quando já temos topK ou menos
  if (chunks.length <= topK) {
    return chunks;
  }

  const startTime = Date.now();
  const resp = await fetchAuth<{
    chunks: SearchResult[];
    timings: { rerankMs: number; totalMs: number };
    inputCount: number;
    skippedNoText: number;
  }>(ENDPOINT_RERANK, { query, chunks, topK }, "rerankChunksRemoto");

  if (!resp) {
    // Usuário deslogado — fallback: ordem original (não desperdiça os chunks)
    console.warn(
      "⚠️ [ragClient] rerank indisponível (auth) — usando ordem original cortada em topK"
    );
    return chunks.slice(0, topK);
  }

  if (resp.skippedNoText > 0) {
    console.warn(
      `⚠️ [ragClient] rerank descartou ${resp.skippedNoText} chunks sem texto`
    );
  }

  const elapsed = Date.now() - startTime;
  console.log(
    `🌐 [ragClient] rerank: ${resp.inputCount} → ${resp.chunks.length} em ${elapsed}ms ` +
      `(server: rerank=${resp.timings.rerankMs}ms)`
  );

  return resp.chunks;
}
